import { useTranslation } from 'react-i18next';

import { useModals } from '../../app/Modals';
import logoBlack from '../../assets/logo-black.webp';
import logoWhite from '../../assets/logo-white.webp';
import { resetConsent } from '../../lib/consent';
import { settingString, socialLinks, useSettings } from '../../lib/settings';
import { LocaleLink } from '../navigation/LocaleLink';

/**
 * SiteFooter — §25.2, column contents per §5.
 * White rounded-3xl card, three link columns, socials, bottom legal bar.
 * Socials and the configurable attribution text come from Settings (§6.18), later in M4.
 */
export function SiteFooter() {
  const { t } = useTranslation();
  const modals = useModals();
  const { data: settings } = useSettings();
  const year = new Date().getFullYear();

  const email = settingString(settings, 'contact.email');
  const phone = settingString(settings, 'contact.phone');
  const address = settingString(settings, 'contact.address');
  // Drop unconfigured socials rather than rendering a link to nowhere.
  const socials = socialLinks(settings).filter((s) => s.url !== '');
  const attribution = settingString(
    settings,
    'site.footer_attribution',
    `© ${year} Samtskhe-Javakheti DMO`,
  );

  return (
    <footer className="px-4 pb-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl rounded-3xl border border-line bg-panel p-8 shadow-soft dark:shadow-soft-dark">
        <div className="flex flex-col gap-8 md:flex-row md:items-start md:justify-between">
          <div className="flex flex-col items-start gap-4">
            <img src={logoBlack} alt="Visit Samtskhe-Javakheti" className="h-12 w-auto dark:hidden" />
            <img src={logoWhite} alt="Visit Samtskhe-Javakheti" className="hidden h-12 w-auto dark:block" />
            {/* Opens the contact modal (§25.5). */}
            <button
              type="button"
              onClick={() => modals.open('contact')}
              className="rounded-pill bg-accent px-4 py-2 text-sm font-medium text-accent-contrast transition-opacity hover:opacity-90"
            >
              {t('footer.contactUs')}
            </button>
          </div>

          <div className="grid grid-cols-1 gap-8 sm:grid-cols-3">
            {/* Samtskhe-Javakheti DMO column (§7.11). */}
            <div>
              <h2 className="mb-3 text-sm font-semibold text-content">{t('footer.dmo')}</h2>
              <ul className="space-y-2 text-sm text-content-muted">
                <li>
                  <LocaleLink to="/destination-management/about" className="hover:text-accent">
                    {t('footer.aboutDmo')}
                  </LocaleLink>
                </li>
                <li>
                  <LocaleLink to="/destination-management/news" className="hover:text-accent">
                    {t('footer.news')}
                  </LocaleLink>
                </li>
                <li>
                  <LocaleLink to="/destination-management/projects" className="hover:text-accent">
                    {t('footer.projects')}
                  </LocaleLink>
                </li>
                <li>
                  <LocaleLink to="/destination-management/reports" className="hover:text-accent">
                    {t('footer.reports')}
                  </LocaleLink>
                </li>
                <li>
                  <LocaleLink to="/blog" className="hover:text-accent">
                    {t('footer.blog')}
                  </LocaleLink>
                </li>
              </ul>
            </div>
            {/* Offers column — five coming-soon pages (§7.11, §7.19). */}
            <div>
              <h2 className="mb-3 text-sm font-semibold text-content">{t('footer.offers')}</h2>
              <ul className="space-y-2 text-sm text-content-muted">
                {(['meetings', 'sports', 'industry', 'travelers', 'weddings'] as const).map((slug) => (
                  <li key={slug}>
                    <LocaleLink to={`/offers/${slug}`} className="hover:text-accent">
                      {t(`footer.offer_${slug}`)}
                    </LocaleLink>
                  </li>
                ))}
              </ul>
            </div>
            {/* Get In Touch — live contact details + socials from Settings (§6.18). */}
            <div>
              <h2 className="mb-3 text-sm font-semibold text-content">{t('footer.getInTouch')}</h2>
              <ul className="space-y-2 text-sm text-content-muted">
                {email && (
                  <li>
                    <a href={`mailto:${email}`} className="hover:text-accent">{email}</a>
                  </li>
                )}
                {phone && (
                  <li>
                    <a href={`tel:${phone.replace(/\s/g, '')}`} className="hover:text-accent">{phone}</a>
                  </li>
                )}
                {address && <li>{address}</li>}
              </ul>

              {/* Only configured socials are shown — an unset link is omitted, never dead. */}
              {socials.length > 0 && (
                <div className="mt-4 flex gap-2">
                  {socials.map(({ key, url }) => (
                    <a
                      key={key}
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={t(`home.social_${key}`)}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-pill border border-line text-xs uppercase text-content-muted transition-colors hover:bg-content/5 hover:text-content"
                    >
                      <span aria-hidden="true">{key[0]}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-col gap-3 border-t border-line pt-6 text-sm text-content-muted sm:flex-row sm:items-center sm:justify-between">
          {/* Configurable attribution text (§6.18, §8.8). */}
          <p>{attribution}</p>
          <nav aria-label="Legal" className="flex flex-wrap gap-4">
            <LocaleLink to="/privacy-policy" className="hover:text-accent">
              {t('footer.privacyPolicy')}
            </LocaleLink>
            <LocaleLink to="/terms-of-service" className="hover:text-accent">
              {t('footer.termsOfService')}
            </LocaleLink>
            <LocaleLink to="/cookie-policy" className="hover:text-accent">
              {t('footer.cookiePolicy')}
            </LocaleLink>
            {/*
              Reopens the consent banner. This is not a nicety: withdrawing consent has to be as
              easy as giving it, and once a decision is recorded the banner never returns on its
              own — so without this there would be no way back. A button, not a link, because it
              changes state rather than navigating.
            */}
            <button type="button" onClick={resetConsent} className="text-left hover:text-accent">
              {t('cookies.manage')}
            </button>
          </nav>
        </div>
      </div>
    </footer>
  );
}
